/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { theme } from "@nordcloud/gnui";
import { ResourceComplianceStatus } from "~/generated/graphql";
import {
  ComplianceStatus,
  ComplianceStatusDetail,
  ResourceUpdateStatus,
  StatusDetail,
} from "./types";

export const statusDetails: Record<ResourceUpdateStatus, StatusDetail> = {
  TOTAL: {
    label: "Total Resources",
    color: theme.color.text.text01,
    icon: "statusOnHold",
    spinnerColor: undefined,
  },
  [ResourceUpdateStatus.PendingUpdates]: {
    label: "Pending Updates",
    color: theme.colors.danger,
    icon: "statusError",
    spinnerColor: "danger",
  },
  [ResourceUpdateStatus.Failed]: {
    label: "Action Failed",
    color: theme.colors.danger,
    icon: "statusError",
    spinnerColor: "danger",
  },
  [ResourceUpdateStatus.NoUpdatesData]: {
    label: "No Updates Data",
    color: theme.colors.warning,
    icon: "statusWarning",
    spinnerColor: "warning",
  },
  [ResourceUpdateStatus.InProgress]: {
    label: "Action In Progress",
    color: theme.colors.notification,
    icon: "statusInProgress",
    spinnerColor: "notification",
  },
  [ResourceUpdateStatus.NoPendingUpdates]: {
    label: "No Pending Updates",
    color: theme.colors.success,
    icon: "statusSuccess",
    spinnerColor: "success",
  },
} as const;

export const updateStatusCardTypes = Object.keys(
  statusDetails
) as ResourceUpdateStatus[];

export const complianceStatusDetails: Record<
  ComplianceStatus,
  ComplianceStatusDetail
> = {
  [ResourceComplianceStatus.Compliant]: {
    label: "Compliant",
    iconColor: theme.colors.success,
    icon: "statusSuccess",
    tooltip: "All required updates are successfully installed.",
    uiStatus: "success",
  },
  [ResourceComplianceStatus.NonCompliant]: {
    label: "Non-compliant",
    iconColor: theme.colors.danger,
    icon: "statusError",
    tooltip: "Updates are missing or failed to install.",
    uiStatus: "danger",
  },
  "N/A": {
    label: "N/A",
    iconColor: theme.color.text.text02,
    tooltip:
      "No successful Patch or Scan actions were detected during the reporting period. This usually means the resource is not assigned to an active plan. Assign a plan to generate a compliance status.",
    disabled: true,
  },
} as const;

export const updateStatusInfoTooltip =
  "Reflects the outcome of the single most recent action performed on this resource. It indicates whether the last attempt to Scan or Patch was successful.";

export const complianceInfoTooltip =
  "Reflects package status over the current month. A resource is “Compliant” only if all updates are installed.";
